import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import {
  declineLetterRequest, getSetting, issueLetter, letterData, loadDirectory, loadIssuedLetters, loadLetterRequests,
  loadLetterTemplates, requestLetter, saveLetterTemplate, saveSetting,
  type IssuedLetter, type LetterRequest, type LetterTemplate,
} from "../data";
import { fill, placeholders, printLetter } from "../lib/letters";
import type { DirectoryRow, Workspace } from "../types";

type Tab = "issue" | "requests" | "templates" | "history";
const TABS: [Tab, StringKey][] = [["issue", "tabIssue"], ["requests", "tabLetterRequests"], ["templates", "tabTemplates"], ["history", "tabHistory"]];
const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

async function signatory(companyId: string) {
  const [name, title] = await Promise.all([getSetting(companyId, "letters.signatory_name"), getSetting(companyId, "letters.signatory_title")]);
  return { signatoryName: (name as string) || "", signatoryTitle: (title as string) || "" };
}

/** HR's letters workspace: issue, answer staff requests, edit templates and reprint from history. */
export function Letters({ ws }: { ws: Workspace }) {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>("issue");
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const [templates, setTemplates] = useState<LetterTemplate[]>([]);
  const [preset, setPreset] = useState<{ employment: string; template: string; request?: string; purpose?: string } | null>(null);
  const cid = ws.membership.company_id;

  const reloadTemplates = () => loadLetterTemplates(cid).then(setTemplates);
  useEffect(() => {
    void loadDirectory(cid).then(setPeople);
    void reloadTemplates();
  }, [cid]);

  return (
    <div className="page">
      <div className="tabs" role="tablist">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? "tab current" : "tab"} onClick={() => setTab(k)}>
            {t(label)}
          </button>
        ))}
      </div>
      {tab === "issue" && <IssueLetter ws={ws} people={people} templates={templates.filter((x) => x.active)} preset={preset} onIssued={() => setPreset(null)} />}
      {tab === "requests" && <Requests ws={ws} people={people} templates={templates}
        onIssue={(r) => { setPreset({ employment: r.employment_id, template: r.template_id, request: r.id, purpose: r.purpose ?? undefined }); setTab("issue"); }} />}
      {tab === "templates" && <Templates ws={ws} templates={templates} onSaved={reloadTemplates} />}
      {tab === "history" && <History ws={ws} people={people} />}
    </div>
  );
}

function IssueLetter({ ws, people, templates, preset, onIssued }: {
  ws: Workspace; people: DirectoryRow[]; templates: LetterTemplate[];
  preset: { employment: string; template: string; request?: string; purpose?: string } | null; onIssued: () => void;
}) {
  const { t, pick } = useI18n();
  const [emp, setEmp] = useState(preset?.employment ?? "");
  const [tplId, setTplId] = useState(preset?.template ?? "");
  const [data, setData] = useState<Record<string, string> | null>(null);
  const [extra, setExtra] = useState<Record<string, string>>(preset?.purpose ? { addressee: preset.purpose } : {});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const tpl = templates.find((x) => x.id === tplId);
  const company = ws.membership.company;

  useEffect(() => {
    if (preset) { setEmp(preset.employment); setTplId(preset.template); }
  }, [preset]);
  useEffect(() => {
    setData(null);
    if (!emp) return;
    letterData(emp).then(setData).catch((e) => setMsg({ ok: false, text: errText(e, t("errorGeneric")) }));
  }, [emp]);

  const blanks = useMemo(() => (tpl && data ? placeholders(tpl.body_en, tpl.body_ar).filter((k) => !data[k]) : []), [tpl, data]);
  const values = { ...(data ?? {}), ...extra };
  const en = tpl ? fill(tpl.body_en, values) : "";
  const ar = tpl ? fill(tpl.body_ar, values) : "";
  const ready = !!tpl && !!data && blanks.every((k) => (extra[k] ?? "").trim());

  async function issue(e: FormEvent) {
    e.preventDefault();
    if (!tpl || !data) return;
    setBusy(true);
    setMsg(null);
    const w = window.open("", "_blank");
    try {
      const ref = await issueLetter(emp, tpl.id, tpl.name_en, en, ar, preset?.request);
      printLetter({ company: company.name, companyAr: company.name_ar ?? data.company_name_ar ?? "", reference: ref, date: data.today,
        title: tpl.name_en, en, ar, signatoryName: data.signatory_name, signatoryTitle: data.signatory_title }, w);
      setMsg({ ok: true, text: `${t("issued")}: ${ref}` });
      setExtra({});
      onIssued();
    } catch (err) {
      w?.close();
      setMsg({ ok: false, text: errText(err, t("errorGeneric")) });
    } finally { setBusy(false); }
  }

  return (
    <form className="card" onSubmit={issue}>
      <div className="field-row">
        <label className="field"><span>{t("employee")}</span>
          <select id="lt-emp" value={emp} onChange={(e) => setEmp(e.target.value)} required>
            <option value="">—</option>
            {people.map((p) => <option key={p.employment_id} value={p.employment_id}>{pick(p.full_name, p.full_name_ar)} · {p.employee_number}</option>)}
          </select></label>
        <label className="field"><span>{t("letterTemplate")}</span>
          <select id="lt-tpl" value={tplId} onChange={(e) => setTplId(e.target.value)} required>
            <option value="">—</option>
            {templates.map((x) => <option key={x.id} value={x.id}>{pick(x.name_en, x.name_ar)}</option>)}
          </select></label>
      </div>
      {blanks.length > 0 && (
        <>
          <h3 className="sub-head">{t("fillBlanks")}</h3>
          <div className="field-row">
            {blanks.map((k) => (
              <label key={k} className="field"><span>{k.replace(/_/g, " ")}</span>
                <input id={`lt-${k}`} value={extra[k] ?? ""} onChange={(e) => setExtra({ ...extra, [k]: e.target.value })} required /></label>
            ))}
          </div>
        </>
      )}
      {tpl && data && (
        <>
          <h3 className="sub-head">{t("preview")}</h3>
          <div className="letter-preview">
            <div className="letter-col" lang="en" dir="ltr"><strong>{tpl.name_en}</strong>{en.split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)}</div>
            {ar.trim() && <div className="letter-col" lang="ar" dir="rtl">{ar.split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)}</div>}
          </div>
        </>
      )}
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      <div className="actions"><button className="btn primary" disabled={!ready || busy}>{t("printAndIssue")}</button></div>
    </form>
  );
}

function Requests({ ws, people, templates, onIssue }: {
  ws: Workspace; people: DirectoryRow[]; templates: LetterTemplate[]; onIssue: (r: LetterRequest) => void;
}) {
  const { t, pick } = useI18n();
  const [rows, setRows] = useState<LetterRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => loadLetterRequests(ws.membership.company_id).then((r) => setRows(r.filter((x) => x.status === "pending"))).catch((e) => setError(e.message));
  useEffect(() => { void refresh(); }, []);
  const person = (id: string) => people.find((p) => p.employment_id === id);
  const tpl = (id: string) => templates.find((x) => x.id === id);

  return (
    <section className="card">
      {error && <p className="alert danger" role="alert">{error}</p>}
      {rows === null ? <p className="muted">{t("loading")}</p> : rows.length === 0 ? <p className="muted">{t("noLetterRequests")}</p> : (
        <ul className="approval-list">
          {rows.map((r) => {
            const p = person(r.employment_id);
            const x = tpl(r.template_id);
            return (
              <li key={r.id} className="approval">
                <div className="approval-main">
                  <span className="strong">{p ? pick(p.full_name, p.full_name_ar) : "—"}</span>
                  <span>{x ? pick(x.name_en, x.name_ar) : ""} · <span className="mono">{r.created_at.slice(0, 10)}</span></span>
                  {r.purpose && <span className="muted small-note">{t("purpose")}: “{r.purpose}”</span>}
                </div>
                <div className="row">
                  <button type="button" className="btn primary small" onClick={() => onIssue(r)}>{t("tabIssue")}</button>
                  <button type="button" className="btn secondary small" onClick={async () => {
                    try { await declineLetterRequest(r.id); await refresh(); } catch (e) { setError(errText(e, t("errorGeneric"))); }
                  }}>{t("decline")}</button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function Templates({ ws, templates, onSaved }: { ws: Workspace; templates: LetterTemplate[]; onSaved: () => Promise<void> }) {
  const { t, pick } = useI18n();
  const [open, setOpen] = useState<string | null>(null);
  return (
    <>
      <LetterSettings ws={ws} />
      <section className="card">
        <p className="muted small-note">{t("placeholdersHelp")}</p>
        <ul className="plain-list">
          {templates.map((x) => (
            <li key={x.id}>
              <button type="button" className="link strong" aria-expanded={open === x.id} onClick={() => setOpen(open === x.id ? null : x.id)}>
                {pick(x.name_en, x.name_ar)}
              </button>
              {!x.active && <span className="badge"> {t("inactive")}</span>}
              {open === x.id && <TemplateEditor tpl={x} onSaved={async () => { await onSaved(); setOpen(null); }} />}
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

function TemplateEditor({ tpl, onSaved }: { tpl: LetterTemplate; onSaved: () => Promise<void> }) {
  const { t } = useI18n();
  const [f, setF] = useState(tpl);
  const [error, setError] = useState<string | null>(null);
  async function save(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await saveLetterTemplate(tpl.id, { name_en: f.name_en, name_ar: f.name_ar, body_en: f.body_en, body_ar: f.body_ar, self_service: f.self_service, active: f.active });
      await onSaved();
    } catch (err) { setError(errText(err, t("errorGeneric"))); }
  }
  return (
    <form className="stack template-editor" onSubmit={save}>
      <div className="field-row">
        <label className="field"><span>{t("nameEn")}</span><input value={f.name_en} onChange={(e) => setF({ ...f, name_en: e.target.value })} required /></label>
        <label className="field"><span>{t("nameAr")}</span><input dir="rtl" value={f.name_ar ?? ""} onChange={(e) => setF({ ...f, name_ar: e.target.value })} /></label>
      </div>
      <label className="field"><span>{t("bodyEn")}</span><textarea rows={8} value={f.body_en} onChange={(e) => setF({ ...f, body_en: e.target.value })} required /></label>
      <label className="field"><span>{t("bodyAr")}</span><textarea rows={8} dir="rtl" value={f.body_ar} onChange={(e) => setF({ ...f, body_ar: e.target.value })} /></label>
      <label className="check"><input type="checkbox" checked={f.self_service} onChange={(e) => setF({ ...f, self_service: e.target.checked })} /> {t("selfService")}</label>
      <label className="check"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> {t("active")}</label>
      {error && <p className="alert danger" role="alert">{error}</p>}
      <div className="actions"><button className="btn primary">{t("save")}</button></div>
    </form>
  );
}

function LetterSettings({ ws }: { ws: Workspace }) {
  const { t } = useI18n();
  const cid = ws.membership.company_id;
  const [f, setF] = useState({ name: "", title: "", prefix: "" });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const canSave = ws.permissions.has("settings.manage");
  useEffect(() => {
    void Promise.all([getSetting(cid, "letters.signatory_name"), getSetting(cid, "letters.signatory_title"), getSetting(cid, "letters.reference_prefix")])
      .then(([name, title, prefix]) => setF({ name: (name as string) ?? "", title: (title as string) ?? "", prefix: (prefix as string) ?? "" }));
  }, [cid]);
  async function save(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try {
      await saveSetting(cid, "letters.signatory_name", f.name);
      await saveSetting(cid, "letters.signatory_title", f.title);
      await saveSetting(cid, "letters.reference_prefix", f.prefix.trim() || "HR");
      setMsg({ ok: true, text: t("saved") });
    } catch (err) { setMsg({ ok: false, text: errText(err, t("errorGeneric")) }); }
  }
  return (
    <form className="card" onSubmit={save}>
      <h2>{t("letterSettings")}</h2>
      <div className="field-row">
        <label className="field"><span>{t("signatoryName")}</span><input value={f.name} disabled={!canSave} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
        <label className="field"><span>{t("signatoryTitle")}</span><input value={f.title} disabled={!canSave} onChange={(e) => setF({ ...f, title: e.target.value })} /></label>
        <label className="field"><span>{t("referencePrefix")}</span><input value={f.prefix} disabled={!canSave} maxLength={10} onChange={(e) => setF({ ...f, prefix: e.target.value })} /></label>
      </div>
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      {canSave && <div className="actions"><button className="btn secondary">{t("save")}</button></div>}
    </form>
  );
}

function History({ ws, people }: { ws: Workspace; people: DirectoryRow[] }) {
  const { t, pick } = useI18n();
  const [rows, setRows] = useState<IssuedLetter[] | null>(null);
  useEffect(() => { void loadIssuedLetters(ws.membership.company_id).then(setRows); }, []);
  const person = (id: string) => people.find((p) => p.employment_id === id);
  return (
    <section className="card">
      {rows === null ? <p className="muted">{t("loading")}</p> : rows.length === 0 ? <p className="muted">{t("noLetters")}</p> : (
        <div className="table-wrap"><table>
          <thead><tr><th>{t("reference")}</th><th>{t("employee")}</th><th className="hide-sm">{t("letterTemplate")}</th><th className="hide-sm">{t("issued")}</th><th /></tr></thead>
          <tbody>{rows.map((r) => {
            const p = person(r.employment_id);
            return (
              <tr key={r.id}>
                <td className="mono">{r.reference_no}</td>
                <td>{p ? pick(p.full_name, p.full_name_ar) : "—"}</td>
                <td className="hide-sm">{r.title}</td>
                <td className="hide-sm mono">{r.issued_at.slice(0, 10)}</td>
                <td className="cell-actions"><ReprintButton ws={ws} letter={r} /></td>
              </tr>
            );
          })}</tbody>
        </table></div>
      )}
    </section>
  );
}

function ReprintButton({ ws, letter }: { ws: Workspace; letter: IssuedLetter }) {
  const { t } = useI18n();
  const company = ws.membership.company;
  async function print() {
    const w = window.open("", "_blank");
    const sig = await signatory(ws.membership.company_id).catch(() => ({ signatoryName: "", signatoryTitle: "" }));
    const d = new Date(letter.issued_at);
    const date = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
    printLetter({ company: company.name, companyAr: company.name_ar ?? "", reference: letter.reference_no, date, title: letter.title,
      en: letter.body_en, ar: letter.body_ar, ...sig }, w);
  }
  return <button type="button" className="btn secondary small" onClick={print}>{t("printAgain")}</button>;
}

/** Staff side: ask HR for a letter and reprint letters already issued. */
export function MyLetters({ ws }: { ws: Workspace }) {
  const { t, pick } = useI18n();
  const cid = ws.membership.company_id;
  const emp = ws.membership.employment_id;
  const [templates, setTemplates] = useState<LetterTemplate[]>([]);
  const [requests, setRequests] = useState<LetterRequest[]>([]);
  const [letters, setLetters] = useState<IssuedLetter[]>([]);
  const [f, setF] = useState({ template: "", purpose: "" });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const refresh = () => {
    if (!emp) return;
    void loadLetterRequests(cid, emp).then(setRequests);
    void loadIssuedLetters(cid, emp).then(setLetters);
  };
  useEffect(() => { void loadLetterTemplates(cid).then((x) => setTemplates(x.filter((y) => y.active && y.self_service))); refresh(); }, [cid, emp]);

  if (!emp) return <div className="page"><p className="alert info">{t("linkFirst")}</p></div>;

  async function send(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try { await requestLetter(f.template, f.purpose); setMsg({ ok: true, text: t("requestSent") }); setF({ template: "", purpose: "" }); refresh(); }
    catch (err) { setMsg({ ok: false, text: errText(err, t("errorGeneric")) }); }
  }
  const tplName = (id: string) => { const x = templates.find((y) => y.id === id); return x ? pick(x.name_en, x.name_ar) : ""; };
  const pending = requests.filter((r) => r.status === "pending");

  return (
    <div className="page">
      <form className="card" onSubmit={send}>
        <h2>{t("requestLetter")}</h2>
        <div className="field-row">
          <label className="field"><span>{t("letterTemplate")}</span>
            <select id="ml-tpl" value={f.template} onChange={(e) => setF({ ...f, template: e.target.value })} required>
              <option value="">—</option>
              {templates.map((x) => <option key={x.id} value={x.id}>{pick(x.name_en, x.name_ar)}</option>)}
            </select></label>
          <label className="field"><span>{t("purpose")}</span>
            <input id="ml-purpose" value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} /></label>
        </div>
        {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
        <div className="actions"><button className="btn primary" disabled={!f.template}>{t("requestLetter")}</button></div>
        {pending.length > 0 && (
          <ul className="plain-list">
            {pending.map((r) => <li key={r.id}>{tplName(r.template_id)} · <span className="mono">{r.created_at.slice(0, 10)}</span> <span className="badge info">{t("pending")}</span></li>)}
          </ul>
        )}
      </form>
      <section className="card">
        <h2>{t("myLetters")}</h2>
        {letters.length === 0 ? <p className="muted">{t("noLetters")}</p> : (
          <ul className="plain-list">
            {letters.map((l) => (
              <li key={l.id} className="row spread">
                <span><span className="strong">{l.title}</span> · <span className="mono">{l.reference_no}</span> · <span className="mono">{l.issued_at.slice(0, 10)}</span></span>
                <ReprintButton ws={ws} letter={l} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
